import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Gift, PartyPopper, CalendarClock, X } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import SavedCardsPanel from "@/components/SavedCardsPanel";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { money } from "@/lib/format";

const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/69c3e51b09d578df3773fc1f5bf83b8908cfaee3362d0b20a018b9936a5c6470.png";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function Memberships() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const { data: tiers = [], isLoading } = useQuery({ queryKey: ["memberships"], queryFn: () => api.get("/memberships").then((r) => r.data) });
  const { data: cardInfo } = useQuery({ queryKey: ["my-saved-card"], queryFn: () => api.get("/memberships/my-card").then((r) => r.data), enabled: !!user });
  const hasCard = !!cardInfo?.has_card;

  const subscribeOneTap = async (t) => {
    if (!user) { nav(`/login?next=/memberships`); return; }
    try {
      setBusy(t.id);
      const { data } = await api.post(`/memberships/${t.id}/subscribe-saved-card`);
      if (data.ok) { setConfirm(data); }
      else if (data.needs_card) toast.error(data.message || "No card on file yet.");
      else toast.error(data.error || data.message || "Could not complete payment.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };


  const isFree = (t) => t.interval === "free" || Number(t.price) === 0;
  const priceLabel = (t) => (isFree(t) ? "Free" : `${money(t.price)}`);
  const periodLabel = (t) => (isFree(t) ? "" : t.interval === "year" ? "/year" : "/month");

  const subscribe = async (t) => {
    if (!user) { nav(`/login?next=/memberships`); return; }
    try {
      setBusy(t.id);
      const { data } = await api.post(`/memberships/${t.id}/subscribe`, { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  return (
    <div data-testid="memberships-page">
      <Seo
        title="Memberships"
        description="Compare Enhancing Your World memberships — free Discovery access, Community membership with circles and member pricing, and paths for Enhancers in Newport and Verona, KY and online."
        path="/memberships"
      />
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="upgrade-confirm-modal">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 text-center relative">
            <button onClick={() => setConfirm(null)} data-testid="upgrade-confirm-close" className="absolute top-5 right-5 text-[#8FA89B] hover:text-[#1A242B]"><X size={20} /></button>
            <div className="mx-auto w-16 h-16 rounded-full bg-[#5C8A72]/15 flex items-center justify-center mb-5">
              <PartyPopper size={30} className="text-[#5C8A72]" />
            </div>
            <h3 className="font-serif text-2xl text-[#144F75]" data-testid="upgrade-confirm-title">You're now a {confirm.plan} member!</h3>
            <p className="text-[#4A5D6B] mt-2">Welcome deeper into the ecosystem. Your card on file was charged
              {confirm.amount ? ` ${money(Number(confirm.amount))}/${confirm.interval === "year" ? "year" : "month"}` : ""}.</p>
            {confirm.next_billing && (
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#4A5D6B] bg-[#F9F7F3] border border-[#E5E0D8] rounded-full px-4 py-2" data-testid="upgrade-confirm-billing">
                <CalendarClock size={16} className="text-[#144F75]" /> Renews {fmtDate(confirm.next_billing)}
              </div>
            )}
            <div className="mt-7 flex gap-3">
              <button onClick={() => nav("/dashboard")} data-testid="upgrade-confirm-dashboard" className="eyw-btn w-full justify-center">Go to my account</button>
              <button onClick={() => setConfirm(null)} data-testid="upgrade-confirm-stay" className="eyw-btn-ghost w-full justify-center">Stay here</button>
            </div>
          </div>
        </div>
      )}
      <PageHero
        label="Membership"
        title="Join at the level that fits you."
        subtitle="Discovery is free and gets you started. Community adds circles, gatherings and member pricing. Enhancer membership is for practitioners who want to share their work."
        image={HERO}
        testid="memberships-hero"
      />
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#144F75]" /></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 items-stretch">
            {tiers.map((t) => {
              const bundled = [...(t.included_services || []), ...(t.included_events || [])];
              return (
                <div key={t.id} data-testid={`tier-${t.key || t.id}`}
                  className={`eyw-card p-8 flex flex-col ${t.highlight ? "ring-2 ring-[#D98E73] relative" : ""}`}>
                  {t.highlight && <span className="absolute -top-3 left-8 bg-[#D98E73] text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>}
                  <h3 className="font-serif text-2xl text-[#144F75]">{t.name}</h3>
                  <p className="text-[#4A5D6B] text-sm mt-1">{t.tagline}</p>
                  <div className="mt-5 flex items-end gap-1">
                    <span className="font-serif text-4xl text-[#1A242B]">{priceLabel(t)}</span>
                    <span className="text-[#4A5D6B] text-sm mb-1">{periodLabel(t)}</span>
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {(t.features || []).map((f, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#4A5D6B]">
                        <Check size={17} className="text-[#144F75] shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                    {bundled.length > 0 && (
                      <li className="flex gap-2 text-sm text-[#1A242B] font-medium pt-2 border-t border-[#EFEAE1] mt-2">
                        <Gift size={17} className="text-[#D98E73] shrink-0 mt-0.5" />
                        <span>Includes {bundled.map((b) => b.title).join(", ")} at no extra cost</span>
                      </li>
                    )}
                  </ul>
                  {isFree(t) ? (
                    <Link to={t.cta_to || "/join"} data-testid={`tier-cta-${t.key || t.id}`} className={`mt-7 ${t.highlight ? "eyw-btn" : "eyw-btn-ghost"} w-full justify-center`}>
                      {t.cta_label || "Join Free"}
                    </Link>
                  ) : (
                    <div className="mt-7 space-y-2">
                      {hasCard && (
                        <button onClick={() => subscribeOneTap(t)} disabled={busy === t.id} data-testid={`tier-onetap-${t.key || t.id}`}
                          className="eyw-btn w-full justify-center disabled:opacity-60">
                          {busy === t.id ? <Loader2 size={16} className="animate-spin" /> : <>⚡ Upgrade with card on file</>}
                        </button>
                      )}
                      <button onClick={() => subscribe(t)} disabled={busy === t.id} data-testid={`tier-cta-${t.key || t.id}`}
                        className={`${hasCard ? "eyw-btn-ghost" : (t.highlight ? "eyw-btn" : "eyw-btn-ghost")} w-full justify-center disabled:opacity-60`}>
                        {busy === t.id ? <Loader2 size={16} className="animate-spin" /> : (hasCard ? "Use a different card" : (t.cta_label || "Subscribe"))}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="text-center text-[#8FA89B] text-sm mt-10 max-w-2xl mx-auto">
          Community Membership includes designated recurring community offerings. It does not automatically include
          every retreat, ceremony, premium workshop, or special event unless marked as included.
        </p>
        {user && (
          <div className="max-w-2xl mx-auto mt-12" data-testid="memberships-cards">
            <div className="eyw-card p-8">
              <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2">Your payment methods</h2>
              <p className="text-sm text-[#4A5D6B] mt-1 mb-2">Save a card here to upgrade in one tap — no front desk needed.</p>
              <SavedCardsPanel compact />
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
